export const PWA_SERVICE_WORKER_FEATURE_FLAG = 'PWA_SERVICE_WORKER_ENABLED';

export type ActiveFeatureConfigLike =
  | Record<string, unknown>
  | undefined
  | null;

let inFlightRegistration: Promise<ServiceWorkerRegistration> | null = null;

export const isPwaServiceWorkerEnabled = (
  config: ActiveFeatureConfigLike
): boolean => Boolean(config?.[PWA_SERVICE_WORKER_FEATURE_FLAG]);

/** Benign lifecycle noise (e.g. redundant worker) — do not report to Sentry. */
export const isBenignServiceWorkerError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';

  return /redundant/i.test(message) || /redundant/i.test(name);
};

export const getServiceWorkerPaths = () => {
  const basePath = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/$/, '');
  const swPath = basePath ? `${basePath}/sw.js` : '/sw.js';
  const scope = basePath ? `${basePath}/` : '/';

  return { swPath, scope, basePath };
};

const hasLiveRegistration = (registration: ServiceWorkerRegistration) =>
  Boolean(
    registration.active ?? registration.installing ?? registration.waiting
  );

export const registerServiceWorker =
  async (): Promise<ServiceWorkerRegistration> => {
    const { swPath, scope } = getServiceWorkerPaths();

    const existingRegistration = await navigator.serviceWorker.getRegistration(
      scope
    );
    if (existingRegistration && hasLiveRegistration(existingRegistration)) {
      return existingRegistration;
    }

    if (inFlightRegistration) {
      return inFlightRegistration;
    }

    inFlightRegistration = navigator.serviceWorker
      .register(swPath, {
        scope,
        // sw.js is served with Cache-Control: no-store; bypass HTTP cache for updates.
        updateViaCache: 'none'
      })
      .finally(() => {
        inFlightRegistration = null;
      });

    return inFlightRegistration;
  };

/** @internal Test-only reset for in-flight registration deduplication. */
export const resetPwaRegistrationStateForTests = () => {
  inFlightRegistration = null;
};
